import { routeUtil } from '@cmg/common';
import { DataGridContextProvider } from '@cmg/data-grid';
import { Grid, useDialogState } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { useRoutes } from '../../../../../common/hooks/useRoutes/useRoutes';
import type { RolodexRecordStatus } from '../../../../../graphql';
import { EditInvestorContactDialog } from './components/EditInvestorContactDialog/EditInvestorContactDialog';
import { FirmDetailGridsNav } from './components/FirmDetailGridsNav/FirmDetailGridsNav';
import { FirmsInvestorContactsGrid } from './components/FirmsInvestorContactsGrid/FirmsInvestorContactsGrid';
import type { FirmInvestorContactsGridItemFragment } from './components/FirmsInvestorContactsGrid/graphql/__generated__';
import { FirmsSalesCoverageGrid } from './components/FirmsSalesCoverageGrid/FirmsSalesCoverageGrid';

export type CMRInvestorFirmDetailGridsRouteProps = Readonly<{
  crmIntegrationId?: string;
  firmStatus: RolodexRecordStatus;
  investorContactsCount: number;
  salesCoverageCount: number;
}>;

export function CMRInvestorFirmDetailGridsRoute({
  crmIntegrationId,
  firmStatus,
  investorContactsCount,
  salesCoverageCount,
}: CMRInvestorFirmDetailGridsRouteProps) {
  const { firmId = '' } = useParams<{ firmId: string }>();

  const { isOpen, onClose, onOpen, context } =
    useDialogState<FirmInvestorContactsGridItemFragment>();

  const { crmInvestorFirm, crmInvestorFirmSalesCoverageGrid, crmInvestorFirmInvestorContactsGrid } =
    useRoutes(crmIntegrationId);

  const basePath = crmInvestorFirm.routePath;

  return (
    <Grid item xs={12} minHeight={650}>
      <FirmDetailGridsNav
        firmStatus={firmStatus}
        investorContactsCount={investorContactsCount}
        salesCoverageCount={salesCoverageCount}
      />
      <Routes>
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            crmInvestorFirmSalesCoverageGrid.routePath
          )}
          element={
            <FirmsSalesCoverageGrid
              tenantId={crmIntegrationId}
              firmId={firmId}
              firmStatus={firmStatus}
            />
          }
        />
        <Route
          path={routeUtil.getRelativeRoutePath(
            basePath,
            crmInvestorFirmInvestorContactsGrid.routePath
          )}
          element={
            <DataGridContextProvider>
              <FirmsInvestorContactsGrid
                tenantId={crmIntegrationId}
                firmId={firmId}
                onEditInvestorContact={onOpen}
              />
              <EditInvestorContactDialog
                tenantId={crmIntegrationId}
                firmId={defaultTo(context?.firmId, null)}
                investorEmployeeId={defaultTo(context?.investorEmployeeId, null)}
                isOpen={isOpen}
                onClose={onClose}
                initialValues={context}
              />
            </DataGridContextProvider>
          }
        />
        <Route
          path="*"
          element={
            <Navigate
              to={crmInvestorFirmSalesCoverageGrid.getUrlPath({ crmIntegrationId, firmId })}
              replace
            />
          }
        />
      </Routes>
    </Grid>
  );
}
